import React from 'react';
import { InfoOutlined } from "@mui/icons-material";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Typography
} from "@mui/material";
import { Link } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useAuth, useTablePagination } from "hooks";
import { useLocalTransportDetailContext } from "contexts/LocalTransportDetailContext";
import { useLazyListShipmentsByLocalTransportIdQuery } from "store/services/localTransportApi";


const LocalTransportShipmentList = () => {
  const { localTransportDetail } = useLocalTransportDetailContext();
  const { isVendor, isCustomer, isDriver } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId,
      width: "150px",
    },
    {
      name: "Sender",
      selector: (row) =>
        row?.customer?.name || row?.vendor?.ownerName || "N/A",
      cell: (row) => (
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.customer?.name ||
            <div>
              <span className="text-sm">{row?.vendor?.shopName || "N/A"}</span>
              <br />
              <span className="text-sm text-gray-500 dark:text-gray-300">
                {row?.vendor?.ownerName || "N/A"}
              </span>
            </div>
            || "N/A"}
        </Box>
      ),
      omit: isVendor,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.name || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name || "Loading...",
      cell: (row) => (
        <Box>
          <span>{row?.sourceBranch?.name || <CircularProgress size={12} />}</span>
          {row?.sourceBranch?.code && (
            <Typography variant="caption" color="textSecondary" display="block">
              ({row.sourceBranch.code})
            </Typography>
          )}
        </Box>
      ),
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name || "Loading...",
      cell: (row) => (
        <Box>
          <span>{row?.destinationBranch?.name || <CircularProgress size={12} />}</span>
          {row?.destinationBranch?.code && (
            <Typography variant="caption" color="textSecondary" display="block">
              ({row.destinationBranch.code})
            </Typography>
          )}
        </Box>
      ),
    },
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.shipmentPickupTime} />,
    },
    {
      name: "Weight",
      selector: (row) => row?.weight || "N/A",
      center: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      center: true,
      cell: (row) => {
        const status = row?.status || row?.shipmentStatus || "N/A";
        const statusColors = {
          "Pending": "bg-yellow-700 text-white",
          "Collected": "bg-blue-500 text-white",
          "Loaded": "bg-gray-500 text-white",
          "In Transit": "bg-orange-500 text-white",
          "Delivered": "bg-green-600 text-white",
          "Returned": "bg-red-600 text-white",
          "Cancelled": "bg-gray-700 text-white",
        };
        return (
          <Badge className={`px-1 py-1 rounded-md ${statusColors[status] || "bg-gray-500"}`}>
            {status}
          </Badge>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
            ? "/app/customer/shipment"
            : isDriver
              ? "/app/driver/shipment"
              : "/app/crm/shipment";
        return (
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Link to={`${basePath}/${row?._id}/detail`}>
              <IconButton size="small" aria-label="View Shipment" color="primary">
                <InfoOutlined />
              </IconButton>
            </Link>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipments, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListShipmentsByLocalTransportIdQuery();

  React.useEffect(() => {
    if (!localTransportDetail?._id) return;
    fetchShipments({
      id: localTransportDetail?._id,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, fetchShipments, perPage, localTransportDetail?._id]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        {shipmentRes ? (
          <AppTable
            columns={columns}
            data={shipmentRes?.data || []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={shipmentRes?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
                <Typography variant="body2">No shipments found for this local transport</Typography>
              </Box>
            }
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>Loading Shipments...</Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
    </>
  );
};

export default LocalTransportShipmentList;